import { useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { EndNotes } from "@/components/EndNotes";
import { PageContainer } from "@/components/PageContainer";
import { Select } from "@/components/Select";
import { useFilter } from "@/hooks/useFilter";
import { useUrls } from "@/hooks/useUrls";
import yearOptions from "@/config/trade-network/year";
import sectorOptions from "@/config/trade-network/sectors";
import { getSectorItems } from "@/utils/filter";
import {
  formatRows,
  getSubSectorDisplayName,
  subSectorsSort,
} from "@/utils/format";
import { GeoNetworkChart } from "./GeoNetworkChart";
import { SankeyChart } from "./SankeyChart";
import styles from "./styles.module.scss";

const sectorItems = getSectorItems(sectorOptions);

const schema = [
  { name: "Reporter code" },
  { name: "Partner code" },
  { name: "Sub-sector code" },
  {
    name: "Value",
    format: (value: string) => parseInt(value),
  },
] as const;

const getDataRows = (rows: unknown) => {
  return formatRows(rows, schema);
};

export type DataRow = ReturnType<typeof getDataRows>[number];

export function App() {
  const urls = useUrls();
  const pageSlug = "trade-network";
  const urlInfo = urls[pageSlug] || {};

  const [flows, setFlows] = useState<DataRow[]>([]);
  const [isFlowsLoading, setIsFlowsLoading] = useState(true);

  const { value: year, onChange: onYearChange } = useFilter(
    "year",
    yearOptions
  );
  const { value: sector, onChange: onSectorChange } = useFilter(
    "sector",
    sectorOptions
  );

  const subSectorOptionsSet = new Set<string>();
  flows.forEach((row) => {
    subSectorOptionsSet.add(row["Sub-sector code"]);
  });
  const subSectorOptions = flows.length
    ? Array.from(subSectorOptionsSet).sort(subSectorsSort)
    : [];
  const { value: subSector, onChange: onSubSectorChange } = useFilter(
    "sub-sector",
    subSectorOptions
  );

  useEffect(() => {
    setIsFlowsLoading(true);
    d3.csv(
      `${process.env.ASSET_PATH || ""}/data/trade-network/${sector}-${year}.csv`
    )
      .then((data) => {
        setFlows(getDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setFlows([]);
      })
      .finally(() => {
        setIsFlowsLoading(false);
      });
  }, [year, sector]);

  const [displayFlows, worldFlows] = useMemo(
    () =>
      flows
        .filter((flow) => flow["Sub-sector code"] === subSector)
        .reduce<[DataRow[], DataRow[]]>(
          (filtered, row) => {
            if (
              row["Reporter code"] !== "WLD" &&
              row["Partner code"] !== "WLD"
            ) {
              filtered[0].push(row);
            } else {
              filtered[1].push(row);
            }
            return filtered;
          },
          [[], []]
        ),
    [flows, subSector]
  );

  const topFlowsPercent = useMemo(() => {
    const displayFlowsValue = displayFlows.reduce(
      (total, row) => total + row.Value,
      0
    );
    const worldToWorldFlow = worldFlows.find(
      (flow) =>
        flow["Reporter code"] === "WLD" && flow["Partner code"] === "WLD"
    );

    return worldToWorldFlow?.Value
      ? Math.round((displayFlowsValue / worldToWorldFlow.Value) * 100)
      : 0;
  }, [displayFlows, worldFlows]);

  return (
    <PageContainer
      headline={urlInfo.headline}
      dek={urlInfo.dek}
      footerCTASlug="trade-composition"
      activePageSlug={pageSlug}
      isLoading={isFlowsLoading}
      filters={
        <>
          <Select
            label="Sector"
            items={sectorItems}
            value={sector}
            format="control-bar"
            onChange={(value) => {
              onSectorChange(value);
              onSubSectorChange("");
            }}
          />
          {subSectorOptions.length > 1 && (
            <Select
              label="Sub-sector"
              items={subSectorOptions.map((o) => ({
                value: o,
                label: getSubSectorDisplayName(o),
              }))}
              value={subSector}
              format="control-bar"
              onChange={(value) => onSubSectorChange(value)}
            />
          )}
          <Select
            label="Year"
            items={yearOptions.map((o) => ({ value: o }))}
            value={year}
            format="control-bar"
            onChange={(value) => onYearChange(value)}
          />
        </>
      }
    >
      <div className={styles["geo-network-chart"]}>
        <GeoNetworkChart
          // Set a unique key for this data set to reset state when data changes
          key={displayFlows.reduce(
            (key, flow) =>
              key.concat(
                flow["Reporter code"],
                flow["Partner code"],
                flow["Value"].toString()
              ),
            ""
          )}
          data={displayFlows}
          worldData={worldFlows}
          sector={sector}
          subSector={subSector}
          year={year}
          topFlowsPercent={topFlowsPercent}
        />
      </div>

      <div className={styles["sankey-chart"]}>
        <SankeyChart
          data={displayFlows}
          worldData={worldFlows}
          sector={sector}
          subSector={subSector}
          year={year}
          topFlowsPercent={topFlowsPercent}
        />
      </div>

      <EndNotes mode="collapsed">
        <p>
          Size of circle indicates relative value of each economy’s global
          imports/exports. Thickness of line shows relative size of
          economy-to-economy flows. Top 40 flows are identified based on import
          trade value for the selected sector, with the relevant economies shown
          on either end of the identified flow. Flows are calculated using only
          UN Comtrade import data; displayed export values may differ from other
          visualizations that use both import and export data, as there are
          asymmetries in UN Comtrade import and export datasets.
        </p>
        <p>Source: UN Comtrade (Refreshed 2025-01-30)</p>
      </EndNotes>
    </PageContainer>
  );
}
